/* ═══════════════════════════════════════════════════════════════════
   Browser-side thumbnail generator — zero-cost image optimization.

   • Resizes an image File/Blob to a max width (default 400px)
   • Returns a JPEG Blob (quality 0.72) ~20-40KB instead of multi-MB
   • Used by useUpload to send a `thumbnail` field alongside the
     full-size `file`, so the gallery grid can render the thumb URL.

   Returns null for non-image media types (video / audio) — those
   are handled by the gallery's poster/icon UI.
   ═══════════════════════════════════════════════════════════════════ */

const MAX_WIDTH = 400;
const JPEG_QUALITY = 0.72;

/** Read a File/Blob into an HTMLImageElement off-DOM. */
const fileToImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

/**
 * createThumbnail(file, maxWidth?) → Promise<Blob|null>
 *
 * Returns a small JPEG Blob suitable for gallery grids.  Falls back
 * to `null` if the file is not an image or the browser cannot decode it
 * (caller should then skip the thumbnail upload and let the gallery
 * fall back to the full-resolution image URL).
 */
export const createThumbnail = async (file, maxWidth = MAX_WIDTH) => {
  try {
    if (!file || !file.type || !file.type.startsWith('image')) return null;
    const img = await fileToImage(file);
    const scale = img.width > maxWidth ? maxWidth / img.width : 1;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise((resolve) =>
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', JPEG_QUALITY),
    );
  } catch (_) {
    return null;
  }
};

export default createThumbnail;
